import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client';
import { client } from './lib/apolloClient';
import { AuthProvider } from './context/AuthContext';
import RequireAuth from './components/Auth/RequireAuth';

// Desktop Pages — each becomes its own JS chunk
const Home                 = lazy(() => import('./pages/Home'));
const Login                = lazy(() => import('./pages/Login'));
const Register             = lazy(() => import('./pages/Register'));
const ForgotPassword       = lazy(() => import('./pages/ForgotPassword'));
const Verify               = lazy(() => import('./pages/Verify'));
const Page                 = lazy(() => import('./pages/Page'));
const Category             = lazy(() => import('./pages/Category'));
const Categories           = lazy(() => import('./pages/Categories'));
const Dashboard            = lazy(() => import('./pages/Dashboard'));
const PostAd               = lazy(() => import('./pages/PostAd'));
const MyAds                = lazy(() => import('./pages/MyAds'));
const Favorites            = lazy(() => import('./pages/Favorites'));
const Profile              = lazy(() => import('./pages/Profile'));
const PublicProfile        = lazy(() => import('./pages/PublicProfile'));
const AdDetail             = lazy(() => import('./pages/AdDetail'));
const Search               = lazy(() => import('./pages/Search'));
const Boost                = lazy(() => import('./pages/Boost'));
const BoostPayment         = lazy(() => import('./pages/BoostPayment'));
const AdminBoostManagement = lazy(() => import('./pages/AdminBoostManagement'));
const Stories              = lazy(() => import('./pages/Stories'));
const Messages             = lazy(() => import('./pages/Messages'));
const Terms                = lazy(() => import('./pages/Terms'));
const PrivacyPolicy        = lazy(() => import('./pages/PrivacyPolicy'));
const CookiePolicy         = lazy(() => import('./pages/CookiePolicy'));
const Disclaimer           = lazy(() => import('./pages/Disclaimer'));
const Pricing              = lazy(() => import('./pages/Pricing'));
const SafetyTips           = lazy(() => import('./pages/SafetyTips'));
const PostingRules         = lazy(() => import('./pages/PostingRules'));
const FAQ                  = lazy(() => import('./pages/FAQ'));
const ContactUs            = lazy(() => import('./pages/ContactUs'));
const Kyc                  = lazy(() => import('./pages/Kyc'));
const About                = lazy(() => import('./pages/About'));
const Blog                 = lazy(() => import('./pages/Blog'));
const HowItWorks           = lazy(() => import('./pages/HowItWorks'));
const VideoFeed            = lazy(() => import('./pages/VideoFeed'));
const NotFound             = lazy(() => import('./pages/NotFound'));

// Mobile Pages
const MobileAlerts     = lazy(() => import('./pages/Mobile/MobileAlerts'));
const MobileMenu       = lazy(() => import('./pages/Mobile/MobileMenu'));

const PageLoader: React.FC = () => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '60vh',
    }}
  >
    <div
      className="spinner-border"
      role="status"
      style={{ width: '2.5rem', height: '2.5rem', color: 'var(--pw-primary, #f2541b)' }}
    >
      <span className="visually-hidden">Loading…</span>
    </div>
  </div>
);

/** One responsive route set for phones, tablets and desktops. */
const AppRoutes: React.FC = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/"                    element={<Home />} />
      <Route path="/login"               element={<Login />} />
      <Route path="/register"            element={<Register />} />
      <Route path="/forgot-password"     element={<ForgotPassword />} />
      <Route path="/verify"              element={<Verify />} />
      <Route path="/page/:slug"          element={<Page />} />
      <Route path="/search"              element={<Search />} />
      <Route path="/category/:slug"      element={<Category />} />
      <Route path="/dashboard"           element={<RequireAuth><Dashboard /></RequireAuth>} />
      <Route path="/post-ad"             element={<RequireAuth><PostAd /></RequireAuth>} />
      <Route path="/edit-ad/:id"         element={<RequireAuth><PostAd /></RequireAuth>} />
      <Route path="/kyc"                 element={<RequireAuth><Kyc /></RequireAuth>} />
      <Route path="/my-ads"              element={<RequireAuth><MyAds /></RequireAuth>} />
      <Route path="/favorites"           element={<RequireAuth><Favorites /></RequireAuth>} />
      <Route path="/profile"             element={<RequireAuth><Profile /></RequireAuth>} />
      <Route path="/profile/:username"   element={<PublicProfile />} />
      <Route path="/ad/:slug"            element={<AdDetail />} />
      <Route path="/boost"               element={<RequireAuth><Boost /></RequireAuth>} />
      <Route path="/boost/payment/:id"   element={<RequireAuth><BoostPayment /></RequireAuth>} />
      <Route path="/admin/boosts"        element={<RequireAuth staff><AdminBoostManagement /></RequireAuth>} />
      <Route path="/stories"             element={<Stories />} />
      <Route path="/messages"            element={<RequireAuth><Messages /></RequireAuth>} />
      <Route path="/terms"               element={<Terms />} />
      <Route path="/privacy"             element={<PrivacyPolicy />} />
      <Route path="/privacy-policy"     element={<PrivacyPolicy />} />
      <Route path="/terms-conditions"    element={<Terms />} />
      <Route path="/cookies"             element={<CookiePolicy />} />
      <Route path="/cookie-policy"       element={<CookiePolicy />} />
      <Route path="/disclaimer"          element={<Disclaimer />} />
      <Route path="/pricing"             element={<Pricing />} />
      <Route path="/safety-tips"         element={<SafetyTips />} />
      <Route path="/posting-rules"       element={<PostingRules />} />
      <Route path="/faq"                 element={<FAQ />} />
      <Route path="/contact-us"          element={<ContactUs />} />
      <Route path="/contact"             element={<ContactUs />} />
      <Route path="/blog"                element={<Blog />} />
      <Route path="/blog/:slug"          element={<Blog />} />
      {/* Phone-first screens linked from the drawer and older links */}
      <Route path="/categories"          element={<Categories />} />
      <Route path="/notifications"       element={<MobileAlerts />} />
      <Route path="/alerts"              element={<MobileAlerts />} />
      <Route path="/menu"                element={<MobileMenu />} />
      <Route path="/about"               element={<About />} />
      <Route path="/how-it-works"        element={<HowItWorks />} />
      <Route path="/videos"              element={<VideoFeed />} />
      <Route path="*"                    element={<NotFound />} />
    </Routes>
  </Suspense>
);

const AppContent: React.FC = () => (
  <Router>
    <AppRoutes />
  </Router>
);

const App: React.FC = () => (
  <ApolloProvider client={client}>
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  </ApolloProvider>
);

export default App;
